
    import { mapActions, mapMutations } from 'vuex';
    import has from 'lodash/has';
    import { ValidationProvider } from 'vee-validate';
    import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome';
    import {
        faChevronDown as farChevronDown,
    } from '@fortawesome/pro-regular-svg-icons/faChevronDown';

    import C1TypeaheadMultiSelect from '@/C1Components/C1TypeaheadMultiSelect.vue';
    import C1TagsContainer from '@/C1Components/C1TagsContainer.vue';
    import C1Tag from '@/C1Components/C1Tag.vue';
    import C1HelpText from '@/C1Components/C1HelpText';

    export default {
        name: 'SeekerAttributesTypeahead',

        components: {
            C1HelpText,
            ValidationProvider,
            C1TypeaheadMultiSelect,
            C1TagsContainer,
            C1Tag,
            FontAwesomeIcon,
        },

        props: {
            attributeCode: {
                type: String,
                required: true,
            },

            attributeId: {
                type: Number,
                required: true,
            },

            inputPlaceholder: {
                type: String,
                default: '',
            },

            label: {
                type: String,
                default: '',
            },

            hideLabel: {
                type: Boolean,
                default: false,
            },

            uppercase: {
                type: Boolean,
                default: false,
            },

            allowNew: {
                type: Boolean,
                default: false,
            },

            minItems: {
                type: Number,
                default: 0,
            },

            hideNoResultMessage: {
                type: Boolean,
                default: false,
            },

            noMobileMode: {
                type: Boolean,
                default: false,
            },

            pushItem: {
                type: Object,
                default() {
                    return {};
                },
            },

            validationMessage: {
                type: String,
                default: '',
            },

            immediateValidation: {
                type: Boolean,
                default: false,
            },
        },

        data() {
            return {
                id: `attribute_typeahead_${this.attributeId}_${this._uid}`,
                attributeOptionsSelected: [],
                attributeOptionsSearched: [],
                isContainerOpen: false,
                spinner: {
                    get_attributes: false,
                },
                icons: {
                    farChevronDown,
                },
            };
        },

        computed: {
            attributeSorted() {
                const attr = JSON.parse(JSON.stringify(this.attributeOptionsSelected));
                return attr.sort((a, b) => {
                    if (a.slug < b.slug) { return -1; }
                    if (a.slug > b.slug) { return 1; }
                    return 0;
                });
            },

            attributeShowMoreLess() {
                const attrs = this.attributeSorted;
                return this.isContainerOpen ? attrs : attrs.slice(0, 6);
            },

            noResultText() {
                return `None ${this.localLabel.toLowerCase()} added.`;
            },

            localLabel() {
                if (!this.label) {
                    let output = '';
                    switch (this.attributeId) {
                        default:
                            output = '';
                            break;
                        case 9:
                            output = 'Licenses & Certifications';
                            break;
                        case 10:
                            output = 'Skills';
                            break;
                        case 21:
                            output = 'Perks & benefits';
                            break;
                        case 24:
                            output = 'Languages';
                            break;
                    }

                    return output;
                }

                return this.label;
            },

            localInputPlaceholder() {
                if (!this.inputPlaceholder) {
                    let output = '';
                    switch (this.attributeId) {
                        default:
                            output = '';
                            break;
                        case 9:
                            output = 'Add licenses or certifications eg. forklift license, AWS Certified';
                            break;
                        case 10:
                            output = 'Add skills eg. Time Management, Leadership, Microsoft Excel';
                            break;
                        case 21:
                            output = 'Add perks and benefits eg. Flexible work hours';
                            break;
                        case 24:
                            output = 'Add languages';
                            break;
                    }

                    return output;
                }

                return this.inputPlaceholder;
            },

            fieldValidation() {
                return this.attributeOptionsSelected.length;
            },

            validationRules() {
                return this.minItems ? `min_value:${this.minItems}` : '';
            },

            showMoreText() {
                return this.isContainerOpen ? 'See less' : `See all ${this.attributeCode}`;
            },
        },

        watch: {
            $mq(value) {
                this.isContainerOpen = value !== 'sm';
            },

            pushItem: {
                // eslint-disable-next-line
                handler: function(newItem) {
                    if (newItem.id) {
                        this.addAttributeOption(newItem);
                    }
                },
                deep: true,
            },
        },

        created() {
            this.isContainerOpen = this.$mq !== 'sm';
            this.getAttributes();
        },

        methods: {
            ...mapActions('attribute', {
                $_searchAttributes: 'searchAttributes',
            }),

            ...mapActions('me', {
                $_getAttributes: 'getAttributes',
                $_storeAttribute: 'storeAttribute',
                $_attachAttribute: 'attachAttribute',
                $_detachAttribute: 'detachAttribute',
            }),

            ...mapMutations('me', {
                $_PUT_ATTRIBUTE_OPTION: 'PUT_ATTRIBUTE_OPTION',
                $_DESTROY_ATTRIBUTE_OPTION: 'DESTROY_ATTRIBUTE_OPTION',
                $_SAVE_ATTRIBUTE_OPTION_AFTER_LOGIN: 'SAVE_ATTRIBUTE_OPTION_AFTER_LOGIN',
            }),

            getAttributes() {
                this.spinner.get_attributes = true;
                this.$_getAttributes(this.attributeId).then((response) => {
                    this.attributeOptionsSelected = response.data;
                    this.$emit('onChange', this.attributeOptionsSelected);
                }).finally(() => {
                    this.spinner.get_attributes = false;
                });
            },

            searchAttributes(keyword) {
                this.$_searchAttributes({ code: this.attributeCode, keyword }).then((response) => {
                    this.attributeOptionsSearched = response.data;
                });
            },

            addAttributeOption(attributeOption) {
                if (!has(attributeOption, 'id')) {
                    const ifAttributeOptionExists = this.attributeOptionsSelected.filter(o => o.value === attributeOption);
                    if (!ifAttributeOptionExists.length && this.allowNew) {
                        this.storeAttributeOption(attributeOption);
                    }
                } else {
                    const ifAttributeOptionExists = this.attributeOptionsSelected.filter(o => o.id === attributeOption.id);
                    if (!ifAttributeOptionExists.length) {
                        this.attachAttributeOption(attributeOption);
                    }
                }
                this.validate();
            },

            storeAttributeOption(attributeOption) {
                const data = {
                    attribute_option_value: attributeOption,
                };

                this.$_storeAttribute({ id: this.attributeId, data }).then((response) => {
                    this.attributeOptionsSelected.push(response.data);
                    this.$emit('onChange', this.attributeOptionsSelected);
                    this.$_PUT_ATTRIBUTE_OPTION(response.data);
                });

                this.validate();
            },

            attachAttributeOption(attributeOption) {
                this.attributeOptionsSelected.push(attributeOption);
                this.$emit('onChange', this.attributeOptionsSelected);
                this.$emit('onChangeStart');
                const data = {
                    attribute_option_id: attributeOption.id,
                };

                this.$_attachAttribute({ id: this.attributeId, data }).then((response) => {
                    this.$_PUT_ATTRIBUTE_OPTION(response.data);
                    this.$emit('onChange', this.attributeOptionsSelected);
                    this.$emit('onChangeEnd');
                }).catch(() => {
                    const loc = this.attributeOptionsSelected.findIndex(o => o.id === attributeOption.id);
                    this.attributeOptionsSelected.splice(loc, 1);
                });
                this.validate();
            },

            detachAttributeOption(attributeOption) {
                const attr = this.attributeOptionsSelected.findIndex(o => o.id === attributeOption.id);
                this.attributeOptionsSelected.splice(attr, 1);
                this.$emit('onChange', this.attributeOptionsSelected);
                this.$emit('onChangeStart');
                const data = {
                    attribute_option_ids: [attributeOption.id],
                };

                this.$_detachAttribute({ id: this.attributeId, data }).then(() => {
                    this.$_DESTROY_ATTRIBUTE_OPTION(attributeOption.id);
                    this.$emit('onChange', this.attributeOptionsSelected);
                    this.$emit('onChangeEnd');
                });

                this.validate();
            },

            validate() {
                this.$refs.fieldAttributes.validate();
            },

            toggleContainer() {
                this.isContainerOpen = !this.isContainerOpen;

                if (!this.isContainerOpen) {
                    this.$velocity(document.getElementById(this.id),
                                   'scroll',
                                   {
                                       offset: -20,
                                       duration: 600,
                                   });
                }
            },
        },
    };
